import { getCompanyODataUrl } from '../../config/api';

export interface CompanyOption {
  value: string;
  text: string;
}

function pickStr(r: Record<string, unknown>, pascal: string, camel: string): string | undefined {
  if (Object.prototype.hasOwnProperty.call(r, pascal) && r[pascal] != null) {
    return String(r[pascal]);
  }
  if (Object.prototype.hasOwnProperty.call(r, camel) && r[camel] != null) {
    return String(r[camel]);
  }
  return undefined;
}

/**
 * GET odata/CompanyOData?$select=CompanyId,Name — maps to Syncfusion-style { value, text }.
 */
export async function fetchCompanyOptions(
  getAccessToken: () => Promise<string | undefined>
): Promise<CompanyOption[]> {
  const baseUrl = getCompanyODataUrl();
  if (!baseUrl) {
    throw new Error(
      'API base URL is not configured. Set REACT_APP_API_BASE_URL or uncomment API_BASE_URL in src/config/api.ts.'
    );
  }
  const url = `${baseUrl}?$select=CompanyId,Name`;
  const token = await getAccessToken();
  const headers: HeadersInit = { Accept: 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  const response = await fetch(url, { headers });
  if (!response.ok) {
    const body = await response.text();
    throw new Error(body || `Request failed with status ${response.status}`);
  }
  const body = (await response.json()) as { value?: unknown[] };
  const rows = Array.isArray(body.value) ? body.value : [];
  const out: CompanyOption[] = [];
  for (const row of rows) {
    if (!row || typeof row !== 'object') {
      continue;
    }
    const r = row as Record<string, unknown>;
    const value = pickStr(r, 'CompanyId', 'companyId');
    const text = pickStr(r, 'Name', 'name');
    if (value && text) {
      out.push({ value, text });
    }
  }
  out.sort((a, b) => a.text.localeCompare(b.text, undefined, { sensitivity: 'base' }));
  return out;
}
